import { apiClient, ApiResponse } from "./client";

// ============================================================================
// Department Types & API
// ============================================================================

export interface Department {
    id: number;
    organizationId: number;
    name: string;
    description: string | null;
    memberCount: number;
    isActive: boolean;
    createdAt: string;
}

export interface DepartmentRequest {
    name: string;
    description?: string;
}

export const departmentApi = {
    getAll: () => apiClient.get<ApiResponse<Department[]>>("/b2b/departments"),

    getById: (id: number) =>
        apiClient.get<ApiResponse<Department>>(`/b2b/departments/${id}`),

    create: (data: DepartmentRequest) =>
        apiClient.post<ApiResponse<Department>>("/b2b/departments", data),

    update: (id: number, data: DepartmentRequest) =>
        apiClient.put<ApiResponse<Department>>(`/b2b/departments/${id}`, data),

    delete: (id: number) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/departments/${id}`),
};

// ============================================================================
// Special Group Types & API
// ============================================================================

export interface SpecialGroup {
    id: number;
    organizationId: number;
    name: string;
    description: string | null;
    memberCount: number;
    isActive: boolean;
    createdAt: string;
}

export interface SpecialGroupRequest {
    name: string;
    description?: string;
}

export interface SpecialGroupMember {
    userId: number;
    fullName: string;
    email: string;
    departmentName: string | null;
    joinedAt: string | null;
}

export interface SpecialGroupMemberRequest {
    userIds: number[];
}

export const specialGroupApi = {
    getAll: () => apiClient.get<ApiResponse<SpecialGroup[]>>("/b2b/special-groups"),

    getById: (id: number) =>
        apiClient.get<ApiResponse<SpecialGroup>>(`/b2b/special-groups/${id}`),

    create: (data: SpecialGroupRequest) =>
        apiClient.post<ApiResponse<SpecialGroup>>("/b2b/special-groups", data),

    update: (id: number, data: SpecialGroupRequest) =>
        apiClient.put<ApiResponse<SpecialGroup>>(`/b2b/special-groups/${id}`, data),

    delete: (id: number) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/special-groups/${id}`),

    // 멤버 관리
    getMembers: (groupId: number) =>
        apiClient.get<ApiResponse<SpecialGroupMember[]>>(`/b2b/special-groups/${groupId}/members`),

    addMembers: (groupId: number, userIds: number[]) =>
        apiClient.post<ApiResponse<SpecialGroupMember[]>>(`/b2b/special-groups/${groupId}/members`, { userIds }),

    removeMembers: (groupId: number, userIds: number[]) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/special-groups/${groupId}/members`, { data: { userIds } }),
};

// ============================================================================
// Member Types & API
// ============================================================================

export interface GuardianInfo {
    name?: string;
    phone?: string;
    postalCode?: string;
    addressBase?: string;
    addressDetail?: string;
}

export interface Member {
    userId: number;
    email: string;
    fullName: string;
    departmentId: number | null;
    departmentName: string | null;
    employeeId: string | null;
    position: string | null;
    role: string;
    status: string;
    accountStatus?: string | null;  // PENDING | INVITED | ACTIVE (백엔드에서 user.passwordHash 기준 파생)
    joinedAt: string | null;
    createdAt: string;
    gradeLevel: string | null;  // 학년정보: preschool, elementary1-6, middle1-3, high, university, adult
    classNames: string[] | null;  // 소속 클래스 목록
    linkedProductName: string | null;  // 연결된 상품명 (커리큘럼) - 첫 번째 클래스 기준
    classProductMap: { [className: string]: string | null } | null;  // 클래스명 → 상품명 매핑
    dateOfBirth?: string | null;   // YYYY-MM-DD
    gender?: string | null;        // male | female | other | prefer_not_to_say
    school?: string | null;
    guardian?: GuardianInfo | null;
    lcAdminRoleId?: number | null;     // LC 직급(역할) id (T5)
    lcAdminRoleName?: string | null;   // LC 직급명(실장/교사/보조교사 등). owner는 null (T5)
}

export interface MemberRequest {
    email: string;
    fullName: string;
    password?: string;
    departmentId?: number;
    employeeId?: string;
    position?: string;
    role?: string;
    gradeLevel?: string;  // 학년정보
    dateOfBirth?: string;
    gender?: string;
    school?: string;
    guardian?: GuardianInfo;
    consent?: MemberConsentRequest;
}

export interface MemberConsentRequest {
    termsAgreed: boolean;
    marketingAgreed: boolean;
    termsVersion?: string;
}

export interface MemberUpdateRequest {
    departmentId?: number;
    employeeId?: string;
    position?: string;
    role?: string;
    gradeLevel?: string;  // 학년정보
    fullName?: string;    // 이름 수정
    email?: string;       // 이메일 수정 (로그인 정보 변경됨)
    dateOfBirth?: string;
    gender?: string;
    school?: string;
    guardian?: GuardianInfo;
}

export interface GuardianRegisteredChild {
    studentName: string;
    handle: string;
    dateOfBirth: string | null;
    gradeLabel: string | null;
}

// 클래스 히스토리 관련 타입
export interface ClassHistoryItem {
    classId: number;
    className: string;
    joinedAt: string;
    leftAt: string | null;  // NULL이면 현재 소속 중
    status: string;
}

export interface ClassHistoryResponse {
    userId: number;
    fullName: string;
    history: ClassHistoryItem[];
}

export const memberApi = {
    getAll: () => apiClient.get<ApiResponse<Member[]>>("/b2b/members"),

    getByDepartment: (departmentId: number) =>
        apiClient.get<ApiResponse<Member[]>>(
            `/b2b/members/department/${departmentId}`
        ),

    getById: (userId: number) =>
        apiClient.get<ApiResponse<Member>>(`/b2b/members/${userId}`),

    getChildrenByGuardianPhone: (phone: string) =>
        apiClient.get<ApiResponse<GuardianRegisteredChild[]>>("/b2b/members/guardian-children", {
            params: { phone },
        }),

    create: (data: MemberRequest) =>
        apiClient.post<ApiResponse<Member>>("/b2b/members", data),

    update: (userId: number, data: MemberUpdateRequest) =>
        apiClient.put<ApiResponse<Member>>(`/b2b/members/${userId}`, data),

    delete: (userId: number) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/members/${userId}`),
        
    // 클래스 히스토리 조회
    getClassHistory: (userId: number) =>
        apiClient.get<ApiResponse<ClassHistoryResponse>>(`/b2b/members/${userId}/class-history`),

    resetPassword: (userId: number, newPassword?: string) =>
        apiClient.post<ApiResponse<{ temporaryPassword: string }>>(`/b2b/members/${userId}/reset-password`, { newPassword }),

    resetLearningProgress: (userId: number, reason: string) =>
        apiClient.delete<ApiResponse<ResetLearningProgressResult>>(
            `/b2b/members/${userId}/learning-progress`,
            { data: { reason } },
        ),
};

export interface ResetLearningProgressResult {
    success: boolean;
    message: string;
    deletedCounts: Record<string, number>;
}

// ============================================================================
// Learner Types & API (Class-based)
// ============================================================================

export interface ProgressUpdateRequest {
    videoProgressPercent?: number;
    quizPassed?: boolean;
    essaySubmitted?: boolean;
}

export const learnerApi = {
    getMyClassCourses: () =>
        apiClient.get<ApiResponse<LearnerClassCourse[]>>("/b2b/my/class-courses"),

    getClassCourse: (classProductId: number) =>
        apiClient.get<ApiResponse<LearnerClassCourse>>(`/b2b/my/class-courses/${classProductId}`),

    updateClassProgress: (classProductId: number, data: ProgressUpdateRequest) =>
        apiClient.put<ApiResponse<LearnerClassCourse>>(
            `/b2b/my/class-courses/${classProductId}/progress`,
            data
        ),
};

// Bundle content item for bundle products
export interface BundleContentItem {
    contentId: number;
    contentTitle: string | null;
    contentType: string;
    sequenceOrder: number;
    thumbnailUrl?: string | null;
    moduleCount?: number;
}

// Class-based learner course (includes classId, licenseId, productId for Configuration API)
export interface LearnerClassCourse {
    classProductId: number;
    classId: number;
    className: string;
    licenseId: number;
    productId: number;
    productName: string | null;
    contentId: number | null;
    contentType: string;
    contentTitle: string | null;
    contentDescription: string | null;
    startDate: string | null;
    deadline: string | null;
    videoProgressPercent: number;
    quizPassed: boolean;
    essaySubmitted: boolean;
    isCompleted: boolean;
    completedAt: string | null;
    startedAt: string | null;
    lastAccessedAt: string | null;
    // Bundle product support
    isBundle: boolean;
    bundleContents?: BundleContentItem[];
    courses?: {
        id: number;
        title: string;
        sequenceOrder: number;
        moduleCount: number;
        lessonCount: number;
        thumbnailUrl?: string | null;
    }[];
}

// ============================================================================
// Report Types & API
// ============================================================================

export interface MemberProgress {
    userId: number;
    email: string;
    fullName: string;
    departmentName: string | null;
    totalAssignments: number;
    completedAssignments: number;
    inProgressAssignments: number;
    averageProgress: number;
}

export interface ProgressReport {
    totalMembers: number;
    totalAssignments: number;
    completedCount: number;
    inProgressCount: number;
    notStartedCount: number;
    completionRate: number;
    memberProgress: MemberProgress[];
}

export interface ProductProgressReport {
    productId: number;
    productName: string;
    curriculumTitle: string | null; // 커리큘럼 제목 (보조 정보)
    totalAssigned: number;
    completedCount: number;
    inProgressCount: number;
    notStartedCount: number;
    completionRate: number;
}

export interface DepartmentProgressReport {
    departmentId: number;
    departmentName: string;
    memberCount: number;
    totalAssignments: number;
    completedCount: number;
    inProgressCount: number;
    completionRate: number;
}

export interface IndividualProgressReport {
    userId: number;
    email: string;
    fullName: string;
    departmentName: string | null;
    totalAssignments: number;
    completedAssignments: number;
    inProgressAssignments: number;
    notStartedAssignments: number;
    averageProgress: number;
    completionRate: number;
    lastActivityAt: string | null;
}

export const reportApi = {
    getProgressReport: () =>
        apiClient.get<ApiResponse<ProgressReport>>("/b2b/reports/progress"),
    getProductProgressReport: () =>
        apiClient.get<ApiResponse<ProductProgressReport[]>>(
            "/b2b/reports/progress/by-product"
        ),
    getDepartmentProgressReport: () =>
        apiClient.get<ApiResponse<DepartmentProgressReport[]>>(
            "/b2b/reports/progress/by-department"
        ),
    getIndividualProgressReport: (search?: string) =>
        apiClient.get<ApiResponse<IndividualProgressReport[]>>(
            `/b2b/reports/progress/by-individual${search ? `?search=${encodeURIComponent(search)}` : ""
            }`
        ),
};

// ============================================================================
// Learning Center Class Types & API
// ============================================================================

export interface LcClassLicenseInfo {
    licenseId: number;
    productId: number;
    productName: string | null;
    curriculumTitle: string | null;
    allowConfigEdit?: boolean;
}

export interface LcClass {
    id: number;
    organizationId: number;
    name: string;
    description: string | null;
    schedule: Record<string, unknown> | null;
    teacherUserId: number | null;
    teacherName: string | null;
    maxStudents: number;
    currentStudents: number;
    isActive: boolean;
    startDate: string | null;
    endDate: string | null;
    createdAt: string;
    licenses: LcClassLicenseInfo[] | null;  // 연결된 상품(라이선스) 정보
    deadline: string | null;  // 학습 마감일
    allowSelfRegistration: boolean;  // 학습자 자유 등록 허용
    allowAdminAssignment: boolean;   // Admin 할당 허용
}

export interface LcClassRequest {
    name: string;
    description?: string;
    schedule?: Record<string, unknown>;
    teacherUserId?: number;
    maxStudents?: number;
    startDate?: string;
    endDate?: string;
    licenseIds?: number[];  // 연결할 라이선스(상품) ID 목록
    deadline?: string;  // 학습 마감일
    allowSelfRegistration?: boolean;  // 학습자 자유 등록 허용
    allowAdminAssignment?: boolean;   // Admin 할당 허용
}

export interface LcClassMember {
    userId: number;
    fullName: string;
    email: string;
    status: string;
    notes: string | null;
    joinedAt: string | null;
    leftAt: string | null;  // 클래스 탈퇴일 (NULL이면 현재 소속 중)
    // 커리큘럼 오버라이드 정보
    overrideLicenseId: number | null;
    overrideProductName: string | null;
    overrideCurriculumTitle: string | null;
}

// ============================================================================
// Curriculum Override Types
// ============================================================================

export interface CurriculumOverrideRequest {
    licenseId: number | null;  // null이면 오버라이드 해제
}

export interface CurriculumOverrideResponse {
    userId: number;
    classId: number;
    overrideLicenseId: number | null;
    productName: string | null;
    curriculumTitle: string | null;
}

export interface LcClassMemberRequest {
    userIds: number[];
}

export interface LcClassMemberUpdateRequest {
    status?: string;
    notes?: string;
}

export interface LcClassStats {
    totalClasses: number;
    activeClasses: number;
    totalStudents: number;
}

export const lcClassApi = {
    getAll: () => apiClient.get<ApiResponse<LcClass[]>>("/b2b/classes"),

    getAllIncludingInactive: () => 
        apiClient.get<ApiResponse<LcClass[]>>("/b2b/classes/all"),

    getById: (id: number) =>
        apiClient.get<ApiResponse<LcClass>>(`/b2b/classes/${id}`),

    create: (data: LcClassRequest) =>
        apiClient.post<ApiResponse<LcClass>>("/b2b/classes", data),

    update: (id: number, data: Partial<LcClassRequest>) =>
        apiClient.put<ApiResponse<LcClass>>(`/b2b/classes/${id}`, data),

    delete: (id: number) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/classes/${id}`),

    activate: (id: number) =>
        apiClient.post<ApiResponse<LcClass>>(`/b2b/classes/${id}/activate`),

    // 멤버(학생) 관리
    getMembers: (classId: number) =>
        apiClient.get<ApiResponse<LcClassMember[]>>(`/b2b/classes/${classId}/members`),

    addMembers: (classId: number, userIds: number[], joinedAt?: string, leftAt?: string) =>
        apiClient.post<ApiResponse<LcClassMember[]>>(`/b2b/classes/${classId}/members`, { 
            userIds,
            joinedAt: joinedAt ? `${joinedAt}T00:00:00` : undefined,
            leftAt: leftAt ? `${leftAt}T23:59:59` : undefined,
        }),

    removeMembers: (classId: number, userIds: number[]) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/classes/${classId}/members`, { data: { userIds } }),

    updateMember: (classId: number, userId: number, data: LcClassMemberUpdateRequest) =>
        apiClient.put<ApiResponse<LcClassMember>>(`/b2b/classes/${classId}/members/${userId}`, data),

    // 상품(라이선스) 관리
    getProducts: (classId: number) =>
        apiClient.get<ApiResponse<number[]>>(`/b2b/classes/${classId}/products`),

    addProducts: (classId: number, licenseIds: number[]) =>
        apiClient.post<ApiResponse<LcClassLicenseInfo[]>>(`/b2b/classes/${classId}/products`, { licenseIds }),

    removeProducts: (classId: number, licenseIds: number[]) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/classes/${classId}/products`, { data: { licenseIds } }),

    // 통계
    getStats: () =>
        apiClient.get<ApiResponse<LcClassStats>>("/b2b/classes/stats"),

    // 커리큘럼 오버라이드
    getCurriculumOverride: (classId: number, userId: number) =>
        apiClient.get<ApiResponse<CurriculumOverrideResponse>>(
            `/b2b/classes/${classId}/members/${userId}/curriculum-override`
        ),

    setCurriculumOverride: (classId: number, userId: number, licenseId: number | null) =>
        apiClient.put<ApiResponse<CurriculumOverrideResponse>>(
            `/b2b/classes/${classId}/members/${userId}/curriculum-override`,
            { licenseId }
        ),

    clearCurriculumOverride: (classId: number, userId: number) =>
        apiClient.delete<ApiResponse<CurriculumOverrideResponse>>(
            `/b2b/classes/${classId}/members/${userId}/curriculum-override`
        ),
};

// ============================================================================
// Admin Notes Types & API
// ============================================================================

export interface AdminNote {
    id: number;
    content: string | null;
    color: string;
    isCompleted: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface AdminNoteCreateRequest {
    content?: string;
    color?: string;
}

export interface AdminNoteUpdateRequest {
    content?: string;
    color?: string;
    isCompleted?: boolean;
}

export const adminNoteApi = {
    getAll: () =>
        apiClient.get<ApiResponse<AdminNote[]>>("/v1/b2b/notes"),

    create: (data: AdminNoteCreateRequest) =>
        apiClient.post<ApiResponse<AdminNote>>("/v1/b2b/notes", data),

    update: (id: number, data: AdminNoteUpdateRequest) =>
        apiClient.put<ApiResponse<AdminNote>>(`/v1/b2b/notes/${id}`, data),

    delete: (id: number) =>
        apiClient.delete<ApiResponse<void>>(`/v1/b2b/notes/${id}`),
};
